/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useEffect, useState } from 'react';
import { FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';
import useMyPaymentRate, { OrderPaymentRate } from '../gql/myPaymentRate';
import getCurrentBusinessQuarter from '../../../utils/getCurrentBusinessQuarter';
import useErrorHandler from '../../../utils/userErrorHandler';
import getYearsForSelect from '../../../utils/getYearsForSelect';
import months from '../../../utils/months';

type PaymentRateProps = {
    quarterly?: boolean;
    upliftData: (data: OrderPaymentRate) => void;
    comparisonBlockData: OrderPaymentRate;
};

const PaymentRate: React.FC<PaymentRateProps> = ({ quarterly, upliftData, comparisonBlockData }) => {
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [quarter, setQuarter] = useState(getCurrentBusinessQuarter());
    const myPaymentRate = useMyPaymentRate({ year: year, month: month, quarter: quarter });
    useErrorHandler(myPaymentRate.error);

    useEffect(() => {
        myPaymentRate.refetch();
    }, [year, month, quarter]);

    useEffect(() => {
        if (myPaymentRate.data) upliftData(myPaymentRate.data);
    }, [myPaymentRate.data]);

    const getProgressionPercentage = (progress: number, target: number): number => {
        return Math.floor((progress / target) * 100);
    };

    return (
        <div style={{ width: '100%' }}>
            <FormControl style={{ width: '50%' }}>
                <InputLabel id="year-select">Év</InputLabel>
                <Select label="Év" id="year-select" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                    {getYearsForSelect().map((year) => (
                        <MenuItem key={year} value={year}>
                            {year}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {!quarterly && (
                <FormControl style={{ width: '50%' }}>
                    <InputLabel id="month-select">Hónap</InputLabel>
                    <Select label="Hónap" id="month-select" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
                        {months.map((month) => (
                            <MenuItem key={month.number} value={month.number}>
                                {month.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}

            {quarterly && (
                <FormControl style={{ width: '50%' }}>
                    <InputLabel id="quarter-select">Negyedév</InputLabel>
                    <Select label="Negyedév" id="quarter-select" value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
                        <MenuItem value={1}>Első negyedév</MenuItem>
                        <MenuItem value={2}>Második negyedév</MenuItem>
                        <MenuItem value={3}>Harmadik negyedév</MenuItem>
                        <MenuItem value={4}>Negyedik negyedév</MenuItem>
                    </Select>
                </FormControl>
            )}

            {!quarterly && (
                <div css={resultsDisplayStyles}>
                    <Typography>
                        Havi kifizetett részletek: {myPaymentRate.data?.monthly.totalPayed} Ft{' '}
                        <span style={{ fontWeight: 700, color: getProgressionPercentage(myPaymentRate.data?.monthly.totalPayed as number, myPaymentRate.data?.monthly.totalPrice as number) >= 100 ? '#2e7d32' : '#d32f2f' }}>
                            ({getProgressionPercentage(myPaymentRate.data?.monthly.totalPayed as number, myPaymentRate.data?.monthly.totalPrice as number)}%)
                        </span>
                    </Typography>
                    <Typography>Havi összes értékesítés: {myPaymentRate.data?.monthly.totalPrice} Ft</Typography>
                </div>
            )}

            {quarterly && (
                <div css={resultsDisplayStyles}>
                    <Typography>
                        Negyedéves kifizetett részletek: {myPaymentRate.data?.quarterly.totalPayed} Ft{' '}
                        <span style={{ fontWeight: 700, color: getProgressionPercentage(myPaymentRate.data?.quarterly.totalPayed as number, myPaymentRate.data?.quarterly.totalPrice as number) >= 100 ? '#2e7d32' : '#d32f2f' }}>
                            ({getProgressionPercentage(myPaymentRate.data?.quarterly.totalPayed as number, myPaymentRate.data?.quarterly.totalPrice as number)}%)
                        </span>
                    </Typography>
                    <Typography>Negyedéves összes értékesítés: {myPaymentRate.data?.quarterly.totalPrice} Ft</Typography>
                </div>
            )}

            {!quarterly && (myPaymentRate.data?.monthly.totalPayed as number) > comparisonBlockData.monthly.totalPayed && (
                <div css={resultContainerStyles}>
                    <Typography fontWeight={700} variant="body1" style={{ color: '#2e7d32' }}>
                        Ebben az időszakban ennyivel:
                    </Typography>
                    <Typography fontWeight={700} variant="h4" style={{ color: '#2e7d32' }}>
                        +{(myPaymentRate.data?.monthly.totalPayed as number) - comparisonBlockData.monthly.totalPayed} Ft
                    </Typography>
                    <Typography fontWeight={700} variant="body1" style={{ color: '#2e7d32' }}>
                        több a kifizetett összeg!
                    </Typography>
                </div>
            )}

            {quarterly && (myPaymentRate.data?.quarterly.totalPayed as number) > comparisonBlockData.quarterly.totalPayed && (
                <div css={resultContainerStyles}>
                    <Typography fontWeight={700} variant="body1" style={{ color: '#2e7d32' }}>
                        Ebben az időszakban ennyivel:
                    </Typography>
                    <Typography fontWeight={700} variant="h4" style={{ color: '#2e7d32' }}>
                        +{(myPaymentRate.data?.quarterly.totalPayed as number) - comparisonBlockData.quarterly.totalPayed} Ft
                    </Typography>
                    <Typography fontWeight={700} variant="body1" style={{ color: '#2e7d32' }}>
                        több a kifizetett összeg!
                    </Typography>
                </div>
            )}
        </div>
    );
};

const resultsDisplayStyles = css`
    display: flex;
    flex-direction: column;
    padding-top: 20px;

    & > * + * {
        margin-top: 20px !important;
    }
`;

const resultContainerStyles = css`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 20px;
    box-sizing: border-box;
    & > * + * {
        margin-top: 10px !important;
    }
`;

export default PaymentRate;
